<script setup lang="ts">
import LawyerCard from '@/widgets/LawyerCard/index';
import { ComplaintModal, prefetchComplaintModal } from '@/widgets/ComplaintModal';
import { useOpenedModals } from '@/shared/composables/useOpenedModals';
import JournalArticle from '@/pages/journalArticle/ui/JournalArticle.vue';
import LawyerArticles from '@/pages/journalArticle/ui/LawyerArticles.vue';
import SimilarArticles from '@/pages/journalArticle/ui/SimilarArticles.vue';
import ArticleComments from '@/pages/journalArticle/ui/ArticleComments.vue';
import ArticleLawyersSections from '@/pages/journalArticle/ui/ArticleLawyersSections.vue';
import JournalArticleSkeleton from '@/pages/journalArticle/ui/Skeleton/index.vue';
import SchemaOrg from '@/shared/ui/SchemaOrg/index';
import useJournalArticle from '@/pages/journalArticle/composables/useJournalArticle';
import useArticleLawyers from '@/pages/journalArticle/composables/useArticleLawyers';
import useBottomSectionsScroll from '@/pages/journalArticle/composables/useBottomSectionsScroll';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';

const {
  isMobile,
  article,
  isShowSkeleton,
  breadcrumbs,
  breadcrumbSchemaData,
  lawyerInfo,
  articleAuthorId,
  articleTopicId,
  journalDetailLink,
  authorArticles,
  isShowLoadMore,
  loadMoreAuthorArticles,
  similarArticles,
  getSimilarArticles,
  pageReady,
  pageError,
} = useJournalArticle();

if (import.meta.server) {
  await pageReady;
  if (pageError.value) {
    throw createError({ statusCode: getErrorStatus(pageError.value), fatal: true });
  }
}

const { lawyersSections, getLawyers } = useArticleLawyers(articleTopicId);
const { openedModals } = useOpenedModals(['complaint'], prefetchComplaintModal);

const commentsRef = ref<HTMLElement | null>(null);
const { isReached: isBottomSectionsRequested } = useBottomSectionsScroll(commentsRef);

watch(isBottomSectionsRequested, (isRequested) => {
  if (!isRequested) return;
  getSimilarArticles();
  getLawyers();
});
</script>

<template>
  <div :class="['journal-article-page', { _mobile: isMobile }]">
    <SchemaOrg v-if="article" type="BreadcrumbList" :data="breadcrumbSchemaData" />
    <div class="journal-article-page__content-wrapper">
      <div v-if="breadcrumbs.length" class="journal-article-page__breadcrumbs">
        <AppBreadcrumbs :breadcrumbs="breadcrumbs" is-show-spread-button />
      </div>
      <JournalArticleSkeleton v-if="isShowSkeleton" />
      <template v-else-if="article">
        <div class="journal-article-page__content">
          <div class="journal-article-page__article-wrapper">
            <JournalArticle :key="article.id" :article="article" />
          </div>
          <aside class="journal-article-page__sidebar">
            <div v-if="lawyerInfo?.id" class="journal-article-page__sidebar-lawyer">
              <LawyerCard
                :data="lawyerInfo"
                is-article
                is-show-title
                is-push-to-feedbacks
              />
            </div>
            <div
              v-if="authorArticles.length"
              class="journal-article-page__sidebar-articles"
            >
              <LawyerArticles
                :lawyer-articles="authorArticles"
                :is-show-load-more="isShowLoadMore"
                @load="loadMoreAuthorArticles"
              />
            </div>
          </aside>
        </div>
        <div
          ref="commentsRef"
          class="journal-article-page__comments"
          data-testid="journal-article-comments"
        >
          <ArticleComments
            :article-id="article.id"
            :article-author-id="articleAuthorId"
            :is-load-requested="isBottomSectionsRequested"
          />
        </div>
        <div v-if="similarArticles.length" class="journal-article-page__similar">
          <SimilarArticles
            :similar-articles="similarArticles"
            :journal-detail-link="journalDetailLink"
          />
        </div>
        <div v-if="lawyersSections.length" class="journal-article-page__lawyers">
          <ArticleLawyersSections :lawyers-sections="lawyersSections" />
        </div>
      </template>
    </div>
    <ComplaintModal v-if="openedModals.complaint" />
  </div>
</template>

<style lang="less">
.journal-article-page {
  padding-bottom: 100px;

  &__content-wrapper {
    .container-d();
    .flex-column(@size-x10);
  }

  &__breadcrumbs {
    margin-top: @size-x10;
  }

  &__content {
    display: flex;
    justify-content: space-between;
    gap: @size-x10;
    padding-bottom: @size-x10;
    border-bottom: 1px solid @color-gallery;
  }

  &__article-wrapper {
    width: 64.7%;
    max-width: 765px;
  }

  &__sidebar {
    .flex-column(@size-x5);

    width: 33.5%;
    height: 100%;
    max-width: 395px;
  }

  &__comments {
    border-bottom: 1px solid @color-gallery;
    padding-bottom: @size-x10;
  }

  &._mobile {
    padding-bottom: 80px;
  }

  &._mobile &__content-wrapper {
    .container-m();
  }

  &._mobile &__content {
    display: contents;
  }

  &._mobile &__breadcrumbs {
    order: 1;
  }

  &._mobile &__article-wrapper {
    order: 2;
    width: 100%;
    max-width: 100%;
  }

  &._mobile &__comments {
    order: 3;
    width: 100%;
  }

  &._mobile &__sidebar {
    order: 4;
    width: 100%;
    max-width: 100%;
  }

  &._mobile &__similar {
    order: 5;
  }

  &._mobile &__lawyers {
    order: 6;
  }
}
</style>
